import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Save, Type, Ruler, Palette, Plus, Trash2, CalendarDays, Settings2, Clock, Sliders, RotateCcw } from "lucide-react";
import Button from "../../components/ui/Button";
import { cartomancerApi, CLOCK_STYLES, TERRAIN_TYPES, WEATHER_TYPES, MARINE_CONDITIONS } from "./api";

/**
 * Full Settings panel (inline overlay above the editor).
 * Manages: clock style, UI scale, pin size, path color, travel modes, terrain modifiers,
 * weather modifiers, calendar (custom + presets), map scale.
 */
export default function SettingsPanel({
  settings,
  map,
  onClose,
  onSaveSettings,
  onSaveMap,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("interface");
  const [s, setS] = useState({ ...(settings || {}) });
  const [m, setM] = useState({ ...(map || {}) });
  const [presets, setPresets] = useState({});

  useEffect(() => {
    cartomancerApi.listCalendarPresets().then((r) => setPresets(r.presets || {})).catch(() => {});
  }, []);

  const updateS = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const updateM = (k, v) => setM((prev) => ({ ...prev, [k]: v }));

  const tabs = [
    { id: "interface", label: t("carto.tabInterface"), icon: <Type className="w-4 h-4" /> },
    { id: "clock", label: t("carto.tabClock"), icon: <Clock className="w-4 h-4" /> },
    { id: "pins", label: t("carto.tabPins"), icon: <Palette className="w-4 h-4" /> },
    { id: "modifiers", label: t("carto.tabModifiers"), icon: <Sliders className="w-4 h-4" /> },
    { id: "map", label: t("carto.tabMap"), icon: <Ruler className="w-4 h-4" /> },
    { id: "travel", label: t("carto.tabTravel"), icon: <Settings2 className="w-4 h-4" /> },
    { id: "calendar", label: t("carto.tabCalendar"), icon: <CalendarDays className="w-4 h-4" /> },
  ];

  const save = () => {
    onSaveSettings(s);
    if (map) onSaveMap(m);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 md:p-6 bg-background/85 backdrop-blur-sm"
      data-testid="settings-panel"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="rune-border bg-card rounded-lg w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="font-display text-2xl text-gold">{t("carto.settingsTitle")}</h3>
            <p className="font-serif-fantasy italic text-sm text-muted-foreground">
              {t("carto.settingsQuote")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            data-testid="settings-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Tabs sidebar */}
          <div className="w-40 border-r border-border p-2 space-y-1 shrink-0">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-[11px] font-heading tracking-widest uppercase transition-all ${
                  tab === t.id
                    ? "bg-gold/20 text-gold border border-gold"
                    : "border border-transparent hover:bg-gold/10 text-muted-foreground"
                }`}
                data-testid={`settings-tab-${t.id}`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {/* Panel content */}
          <div className="flex-1 overflow-y-auto scrollbar-thin p-5">
            {tab === "interface" && (
              <InterfaceTab s={s} updateS={updateS} />
            )}
            {tab === "clock" && (
              <ClockTab s={s} updateS={updateS} />
            )}
            {tab === "pins" && (
              <PinsPathTab s={s} updateS={updateS} />
            )}
            {tab === "modifiers" && (
              <ModifiersTab s={s} updateS={updateS} />
            )}
            {tab === "map" && map && (
              <MapScaleTab m={m} updateM={updateM} />
            )}
            {tab === "travel" && map && (
              <TravelTab m={m} setM={setM} />
            )}
            {tab === "calendar" && map && (
              <CalendarTab m={m} setM={setM} presets={presets} />
            )}
          </div>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>{t("carto.cancel")}</Button>
          <Button variant="primary" size="sm" onClick={save} data-testid="settings-save-btn">
            <Save className="w-4 h-4 mr-2" /> {t("carto.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------- TABS ---------- */

function InterfaceTab({ s, updateS }) {
  const { t } = useTranslation();
  const uiPx = Math.round((s.ui_scale_rem || 1) * 16);
  const textPx = Math.round((s.text_size_rem || 1) * 16);
  return (
    <div className="space-y-6">
      <SliderRow
        label={t("carto.uiScale", { px: uiPx })}
        min={12} max={22} step={1} value={uiPx}
        onChange={(v) => updateS("ui_scale_rem", v / 16)}
        testId="ui-scale-slider"
      />
      <SliderRow
        label={t("carto.textSize", { px: textPx })}
        min={11} max={22} step={1} value={textPx}
        onChange={(v) => updateS("text_size_rem", v / 16)}
        testId="text-size-slider"
      />
      <SelectRow
        label={t("carto.fieldLanguage")}
        value={s.language || "it"}
        options={[{ value: "it", label: "Italiano" }, { value: "en", label: "English" }]}
        onChange={(v) => updateS("language", v)}
      />
      <SelectRow
        label={t("carto.clockFormat")}
        value={s.clock_format || "24h"}
        options={[{ value: "24h", label: t("carto.clock24h") }, { value: "12h", label: t("carto.clock12h") }]}
        onChange={(v) => updateS("clock_format", v)}
      />
    </div>
  );
}

function ClockTab({ s, updateS }) {
  const { t } = useTranslation();
  const clockScalePct = Math.round((s.clock_scale || 1) * 100);
  return (
    <div className="space-y-4">
      <p className="font-serif-fantasy italic text-sm text-muted-foreground">
        {t("carto.clockIntro")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {CLOCK_STYLES.map((cs) => (
          <button
            key={cs.id}
            onClick={() => updateS("clock_style", cs.id)}
            className={`rune-border rounded-md p-4 text-center transition-all ${
              (s.clock_style || "astrolabio") === cs.id
                ? "bg-gold/20 gold-glow"
                : "bg-card/50 hover:bg-gold/10"
            }`}
            data-testid={`clock-style-${cs.id}`}
          >
            <ClockPreview id={cs.id} />
            <p className="font-heading text-xs tracking-widest text-gold uppercase mt-3">
              {cs.label}
            </p>
          </button>
        ))}
      </div>
      <SliderRow
        label={t("carto.clockSize", { pct: clockScalePct })}
        min={0.6} max={1.4} step={0.05} value={s.clock_scale || 1}
        onChange={(v) => updateS("clock_scale", v)}
        testId="clock-scale-slider"
      />
    </div>
  );
}

function ClockPreview({ id }) {
  if (id === "meridiana") {
    return (
      <svg viewBox="0 0 100 60" className="w-full h-20">
        <path d="M 5 55 A 45 45 0 0 1 95 55 Z" fill="rgba(212,175,55,0.15)" stroke="#d4af37" strokeWidth="2" />
        <polygon points="48,55 52,55 50,20" fill="#d4af37" />
        <circle cx="70" cy="30" r="4" fill="#fef3c7" stroke="#d4af37" strokeWidth="1" />
      </svg>
    );
  }
  if (id === "cronografo") {
    return (
      <svg viewBox="0 0 60 60" className="w-full h-20">
        <rect x="10" y="6" width="40" height="4" fill="#78350f" stroke="#d4af37" />
        <rect x="10" y="50" width="40" height="4" fill="#78350f" stroke="#d4af37" />
        <path d="M 15 10 L 45 10 L 32 30 L 32 30 L 45 50 L 15 50 L 28 30 L 28 30 Z" fill="rgba(212,175,55,0.06)" stroke="#d4af37" strokeWidth="1.5" />
        <path d="M 16 10 L 44 10 L 32 26 L 28 26 Z" fill="#d4af37" opacity="0.6" />
        <path d="M 15 50 L 45 50 L 45 40 L 32 32 L 28 32 Z" fill="#d4af37" />
      </svg>
    );
  }
  // astrolabio default
  return (
    <svg viewBox="0 0 60 60" className="w-full h-20">
      <circle cx="30" cy="30" r="26" fill="none" stroke="#d4af37" strokeWidth="2" />
      <circle cx="30" cy="30" r="20" fill="none" stroke="rgba(212,175,55,0.4)" strokeDasharray="2 3" />
      <circle cx="30" cy="30" r="8" fill="#78350f" stroke="#d4af37" strokeWidth="1" />
      <circle cx="30" cy="30" r="3" fill="#fef3c7" />
      <line x1="30" y1="30" x2="30" y2="12" stroke="#d4af37" strokeWidth="2" />
      <line x1="30" y1="30" x2="46" y2="30" stroke="#a78bfa" strokeWidth="1.5" />
    </svg>
  );
}

function PinsPathTab({ s, updateS }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <SliderRow
        label={t("carto.pinSize", { n: (s.pin_size || 1).toFixed(2) })}
        min={0.5} max={2.5} step={0.1} value={s.pin_size || 1}
        onChange={(v) => updateS("pin_size", v)}
        testId="pin-size-slider"
      />
      <SelectRow
        label={t("carto.pinStyle")}
        value={s.pin_style || "shield"}
        options={[
          { value: "shield", label: t("carto.pinShield") },
          { value: "banner", label: t("carto.pinBanner") },
          { value: "seal", label: t("carto.pinSeal") },
        ]}
        onChange={(v) => updateS("pin_style", v)}
      />
      <SliderRow
        label={t("carto.pathPointSize", { n: (s.path_point_size || 1).toFixed(2) })}
        min={0.5} max={2.5} step={0.1} value={s.path_point_size || 1}
        onChange={(v) => updateS("path_point_size", v)}
        testId="path-point-slider"
      />
      <div>
        <p className="font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
          {t("carto.pathColor")}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={s.path_color || "#d4af37"}
            onChange={(e) => updateS("path_color", e.target.value)}
            className="w-14 h-9 rounded border border-gold/40 bg-transparent"
            data-testid="path-color-input"
          />
          <input
            type="text"
            value={s.path_color || "#d4af37"}
            onChange={(e) => updateS("path_color", e.target.value)}
            className="flex-1 bg-input border border-border rounded px-3 py-2 text-sm"
          />
        </div>
      </div>
    </div>
  );
}

function MapScaleTab({ m, updateM }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <p className="font-serif-fantasy italic text-sm text-muted-foreground">
        {t("carto.mapScaleIntro")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label={t("carto.refPixels")}>
          <input
            type="number"
            min="1"
            value={m.scale_pixels || 100}
            onChange={(e) => updateM("scale_pixels", parseFloat(e.target.value) || 1)}
            className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
            data-testid="scale-pixels-input"
          />
        </Field>
        <Field label={t("carto.realDistance")}>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={m.scale_distance || 50}
            onChange={(e) => updateM("scale_distance", parseFloat(e.target.value) || 1)}
            className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
            data-testid="scale-distance-input"
          />
        </Field>
        <Field label={t("carto.unit")}>
          <select
            value={m.scale_unit || "miles"}
            onChange={(e) => updateM("scale_unit", e.target.value)}
            className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
            data-testid="scale-unit-select"
          >
            <option value="miles">{t("carto.unitMiles")}</option>
            <option value="km">{t("carto.unitKm")}</option>
            <option value="leagues">{t("carto.unitLeagues")}</option>
            <option value="hex">{t("carto.unitHex")}</option>
          </select>
        </Field>
      </div>
    </div>
  );
}

function TravelTab({ m, setM }) {
  const { t } = useTranslation();
  const modes = m.travel_modes || [];
  const updateMode = (i, k, v) => {
    const copy = [...modes];
    copy[i] = { ...copy[i], [k]: v };
    setM({ ...m, travel_modes: copy });
  };
  const addMode = () => setM({
    ...m,
    travel_modes: [
      ...modes,
      { id: `mode_${Date.now()}`, name: t("carto.newTravelMode"), icon: "footprints", speed: 24, enabled: true },
    ],
  });
  const removeMode = (i) => setM({ ...m, travel_modes: modes.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-serif-fantasy italic text-sm text-muted-foreground">
          {t("carto.travelIntro")}
        </p>
        <Button variant="secondary" size="sm" onClick={addMode} data-testid="add-mode-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> {t("carto.addTravelMode")}
        </Button>
      </div>
      <div className="space-y-2">
        {modes.map((mode, i) => (
          <div key={mode.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-center p-2 rounded border border-border bg-card/50">
            <input
              className="bg-input border border-border rounded px-2 py-1 text-sm"
              value={mode.name}
              onChange={(e) => updateMode(i, "name", e.target.value)}
              data-testid={`mode-name-${i}`}
            />
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="1"
                value={mode.speed}
                onChange={(e) => updateMode(i, "speed", parseFloat(e.target.value) || 0)}
                className="w-24 bg-input border border-border rounded px-2 py-1 text-sm"
                data-testid={`mode-speed-${i}`}
              />
              <span className="text-xs text-muted-foreground">{m.scale_unit || "mi"}{t("carto.perDay")}</span>
            </div>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={mode.enabled !== false}
                onChange={(e) => updateMode(i, "enabled", e.target.checked)}
              />
              {t("carto.active")}
            </label>
            <button onClick={() => removeMode(i)} className="p-1 text-muted-foreground hover:text-ruby">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarTab({ m, setM, presets }) {
  const { t } = useTranslation();
  const months = m.months || [];
  const applyPreset = (id) => {
    if (presets[id]) setM({ ...m, months: [...presets[id]] });
  };
  const updateMonth = (i, v) => {
    const copy = [...months];
    copy[i] = v;
    setM({ ...m, months: copy });
  };
  const addMonth = () => setM({ ...m, months: [...months, t("carto.monthDefault", { n: months.length + 1 })] });
  const removeMonth = (i) => setM({ ...m, months: months.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      <div>
        <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-2">{t("carto.presetLabel")}</p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(presets).map((id) => (
            <button
              key={id}
              onClick={() => applyPreset(id)}
              className="px-3 py-1 rounded-full border border-gold/40 hover:border-gold hover:bg-gold/10 text-gold font-heading text-[10px] tracking-widest uppercase"
              data-testid={`calendar-preset-${id}`}
            >
              {id}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading text-[10px] tracking-widest text-gold uppercase">{t("carto.monthsLabel", { count: months.length })}</p>
          <Button variant="secondary" size="sm" onClick={addMonth} data-testid="add-month-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> {t("carto.addMonth")}
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {months.map((month, i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                className="flex-1 bg-input border border-border rounded px-2 py-1 text-sm"
                value={month}
                onChange={(e) => updateMonth(i, e.target.value)}
              />
              <button onClick={() => removeMonth(i)} className="p-1 text-muted-foreground hover:text-ruby">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- MODIFIERS TAB ---------- */

function ModifiersTab({ s, updateS }) {
  const { t } = useTranslation();
  const cm = s.custom_modifiers || {};
  const setMod = (kind, id, value) => {
    const next = {
      ...cm,
      [kind]: { ...(cm[kind] || {}), [id]: value },
    };
    updateS("custom_modifiers", next);
  };
  const resetMod = (kind, id) => {
    const kindCopy = { ...(cm[kind] || {}) };
    delete kindCopy[id];
    updateS("custom_modifiers", { ...cm, [kind]: kindCopy });
  };
  const resetAll = () => updateS("custom_modifiers", {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="font-serif-fantasy italic text-sm text-muted-foreground max-w-lg">
          {t("carto.modifiersIntro")}
        </p>
        <Button variant="secondary" size="sm" onClick={resetAll} data-testid="modifiers-reset-all-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t("carto.resetAll")}
        </Button>
      </div>

      <ModGroup
        title={t("carto.modTerrains")}
        list={TERRAIN_TYPES}
        overrides={cm.terrain}
        onChange={(id, v) => setMod("terrain", id, v)}
        onReset={(id) => resetMod("terrain", id)}
        testPrefix="mod-terrain"
      />
      <ModGroup
        title={t("carto.modWeather")}
        list={WEATHER_TYPES}
        overrides={cm.weather}
        onChange={(id, v) => setMod("weather", id, v)}
        onReset={(id) => resetMod("weather", id)}
        testPrefix="mod-weather"
      />
      <ModGroup
        title={t("carto.modMarine")}
        list={MARINE_CONDITIONS}
        overrides={cm.marine}
        onChange={(id, v) => setMod("marine", id, v)}
        onReset={(id) => resetMod("marine", id)}
        testPrefix="mod-marine"
      />
    </div>
  );
}

function ModGroup({ title, list, overrides = {}, onChange, onReset, testPrefix }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-2">{title}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {list.map((item) => {
          const override = overrides?.[item.id];
          const value = typeof override === "number" ? override : item.speedMod;
          const isOverride = typeof override === "number";
          return (
            <div
              key={item.id}
              className="flex items-center gap-2 p-2 rounded border border-border bg-card/50 hover:border-gold/40 transition-colors"
              data-testid={`${testPrefix}-row-${item.id}`}
            >
              <span className="flex-1 text-sm text-parchment">{item.label}</span>
              <input
                type="number"
                min="0.05"
                max="5"
                step="0.05"
                value={value}
                onChange={(e) => onChange(item.id, parseFloat(e.target.value) || 0)}
                className={`w-20 bg-input border rounded px-2 py-1 text-sm text-right ${
                  isOverride ? "border-gold text-gold" : "border-border"
                }`}
                data-testid={`${testPrefix}-${item.id}`}
              />
              <span className="text-[10px] text-muted-foreground">
                (def. ×{item.speedMod})
              </span>
              {isOverride && (
                <button
                  onClick={() => onReset(item.id)}
                  className="p-1 rounded text-muted-foreground hover:text-ruby hover:bg-ruby/10 transition-colors"
                  title={t("carto.resetDefault")}
                  data-testid={`${testPrefix}-reset-${item.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function SliderRow({ label, min, max, step, value, onChange, testId }) {
  return (
    <div>
      <p className="font-heading text-[10px] tracking-widest text-parchment uppercase mb-2">
        {label}
      </p>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-gold"
        data-testid={testId}
      />
    </div>
  );
}

function SelectRow({ label, value, options, onChange }) {
  return (
    <div>
      <p className="font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
        {label}
      </p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-input border border-gold/30 rounded px-3 py-2 text-sm w-full max-w-xs"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
